import { Box, VStack, Text, Flex, Center, Separator } from '@chakra-ui/react';
import { Drawer } from '@halo/ui-react';
import { CloseIcon } from '@halo/ui-chat-react';
import { Fragment } from 'react/jsx-runtime';
import type { User } from '@halo/chat-sdk-react';
import { useActionsStore } from '@/store/actions';
import BackIcon from '@/assets/images/common/svg/back.svg?react';
import ProfileBg from '../ProfileDialog/Profile/images/defaultBg.png';
import { EditFormDialog } from './EditFormDialog';
import { EditItem, useEditItems } from './helpers';
import { Header } from './Header';

interface ProfileEditDialogProps {
  user: User;
  handleUpdateProfileUser: () => void;
  countryLabel: string;
}

function ProfileEditDialog({
  user,
  handleUpdateProfileUser,
  countryLabel,
}: ProfileEditDialogProps) {
  // const { user } = useAppStore();
  const { t } = useTranslation();
  const { setProfileOpen, profileEdit, setProfileEdit } = useActionsStore();
  const [editItem, setEditItem] = useState<EditItem>(EditItem.Name);
  const [editFormVisible, setEditFormVisible] = useState(false);

  const editItems = useEditItems(user, setEditFormVisible, setEditItem, handleUpdateProfileUser);

  const handleBack = useCallback(() => {
    setProfileEdit(false);
    setProfileOpen(true);
  }, [setProfileEdit, setProfileOpen]);

  return (
    <>
      <Drawer.Root open={profileEdit} onOpenChange={(details) => setProfileEdit(details.open)}>
        <Drawer.Backdrop />
        <Drawer.Positioner>
          <Drawer.Content w="480px" maxW="100%">
            <Drawer.Header pt="5" px="5" pb="2.5" justifyContent="space-between">
              <Flex onClick={handleBack} cursor="pointer" alignItems="center" gap="1">
                <BackIcon />
                <Text fontSize="md" fontWeight="semibold">
                  {t('profile.editProfileTitle')}
                </Text>
              </Flex>
              <Center cursor="pointer" onClick={handleBack}>
                <CloseIcon />
              </Center>
            </Drawer.Header>

            <Drawer.Body p={0} bg="bg.subtle" roundedBottomLeft="xl" roundedBottomRight="xl">
              <Box
                pb="5"
                w="full"
                bgImage={`url(${ProfileBg})`}
                bgSize="contain"
                bgPos="center top"
                bgRepeat="no-repeat"
                pt="250px"
              >
                <Header
                  user={user}
                  handleUpdateProfileUser={handleUpdateProfileUser}
                  countryLabel={countryLabel}
                />
                <VStack w="full" px="5">
                  {editItems.map((item) => (
                    <Box key={item.title} w="full">
                      <Text fontSize="xs" my="2.5" color="fg.muted" fontWeight="medium">
                        {item.title}
                      </Text>
                      <Box w="full" bg="bg" rounded="xl" fontSize="sm">
                        {item.items.map((subItem: any, index: number) => (
                          <Fragment key={subItem.label}>
                            {subItem?.children ? (
                              subItem?.children
                            ) : (
                              <>
                                <Flex
                                  justifyContent="space-between"
                                  py="3.5"
                                  px="3"
                                  gap="3"
                                  alignItems="center"
                                  cursor="pointer"
                                  fontWeight="medium"
                                  onClick={() => subItem.onClick()}
                                >
                                  <Text color="fg">{subItem.label}</Text>
                                  <Box color="brand" truncate>
                                    {subItem.value}
                                  </Box>
                                </Flex>
                                {index !== item.items.length - 1 && (
                                  <Separator borderColor="border.subtle" ml="3" mr="1" />
                                )}
                              </>
                            )}
                          </Fragment>
                        ))}
                      </Box>
                    </Box>
                  ))}
                </VStack>
              </Box>
            </Drawer.Body>
          </Drawer.Content>
        </Drawer.Positioner>
      </Drawer.Root>
      <EditFormDialog
        title={`${t('buttons.edit')}  ${t(`profile.${editItem}`)}`}
        item={editItem}
        show={editFormVisible}
        setShow={setEditFormVisible}
        handleUpdateProfileUser={handleUpdateProfileUser}
      />
    </>
  );
}

export { ProfileEditDialog };
